/**
 * Phase 5.5-Lite — Customer pre-pay confirmation modal.
 *
 * Shown after the customer picks "Pay Now · Cash App" and taps "PLACE ORDER".
 * Renders the cashtag deep-link + QR (server-generated). Customer taps the
 * deep-link, completes payment in Cash App, returns and confirms — only then
 * does the order actually submit.
 */
import React from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Smartphone, CheckCircle2, X } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

export function CashAppPrepayModal({
  open,
  onClose,
  amount,
  orderShortId,
  render,           // { configured, display_name, handle, deep_link, qr_data_url }
  loading,
  onConfirmPaid,    // user taps "I've sent the payment" → places the order
  onPayOnDelivery,  // user taps "Pay on delivery instead" → switches method
}) {
  const { isModern } = useTheme();
  if (!open) return null;
  if (isModern) {
    return (
      <ModernCashAppModal {...{ open, onClose, amount, orderShortId, render, loading, onConfirmPaid, onPayOnDelivery }} />
    );
  }
  const configured = render?.configured;
  const displayName = render?.display_name || 'Ash & Merch';
  const handle = render?.handle;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="bg-[#050003] border-[#39FF14] text-[#39FF14] font-mono rounded-none max-w-md"
        data-testid="cashapp-prepay-modal"
      >
        <DialogTitle className="text-[#39FF14] font-mono text-lg uppercase tracking-wider">
          &gt; COMPLETE YOUR PAYMENT_
        </DialogTitle>
        <DialogDescription className="text-[#6B8E73] font-mono text-xs uppercase tracking-wider">
          // Cash App pre-pay
        </DialogDescription>

        {loading && (
          <div className="text-[#A3C7A8] font-mono text-sm py-8 text-center" data-testid="cashapp-prepay-loading">
            // Generating payment link…
          </div>
        )}

        {!loading && !configured && (
          <div className="text-[#FF3B30] font-mono text-xs p-4 border border-[#FF3B30]" data-testid="cashapp-prepay-unconfigured">
            // CASHAPP_HANDLE_NOT_SET — please contact support or pay on delivery.
          </div>
        )}

        {!loading && configured && (
          <div className="space-y-4">
            <div className="bg-[#0C0612] border border-[#2A0F3D] p-4">
              <p className="text-[#6B8E73] font-mono text-xs uppercase">PAY TO</p>
              <p className="text-[#39FF14] font-mono text-2xl mt-1">{displayName}</p>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[#6B8E73] font-mono text-xs uppercase">TOTAL</p>
                  <p className="text-[#39FF14] font-mono text-xl">${Number(amount).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-[#6B8E73] font-mono text-xs uppercase">ORDER #</p>
                  <p className="text-[#39FF14] font-mono text-xl">{orderShortId}</p>
                </div>
              </div>
            </div>

            <Button
              asChild
              className="w-full bg-[#39FF14] hover:bg-[#2AE54A] text-[#050003] font-mono uppercase tracking-wider rounded-none border-0 h-14 text-base"
              data-testid="cashapp-prepay-deeplink"
            >
              <a href={render.deep_link} target="_blank" rel="noopener noreferrer">
                <Smartphone className="w-5 h-5 mr-2" />
                &gt; TAP TO PAY VIA CASH APP
              </a>
            </Button>

            <div className="flex flex-col items-center gap-2 py-2">
              <p className="text-[#6B8E73] font-mono text-xs uppercase">// OR SCAN WITH ANOTHER DEVICE</p>
              {render.qr_data_url && (
                <img
                  src={render.qr_data_url}
                  alt="Cash App payment QR code"
                  className="w-48 h-48 border border-[#2A0F3D] bg-white p-2"
                  data-testid="cashapp-prepay-qr"
                />
              )}
              <p className="text-[#4B6B52] font-mono text-[10px] uppercase">Sending to: {handle}</p>
            </div>

            <div className="bg-[#0C0612] border border-[#2A0F3D] p-3">
              <p className="text-[#A3C7A8] font-mono text-xs">
                // Send <span className="text-[#39FF14]">${Number(amount).toFixed(2)}</span> and include
                {' '}<span className="text-[#39FF14]">Order-{orderShortId}</span> in the note so we can match it up.
              </p>
            </div>

            <div className="flex flex-col gap-2 pt-2">
              <Button
                onClick={onConfirmPaid}
                className="w-full bg-[#39FF14] hover:bg-[#2AE54A] text-[#050003] font-mono uppercase tracking-wider rounded-none border-0 h-12"
                data-testid="cashapp-prepay-confirm"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                &gt; I'VE SENT THE PAYMENT
              </Button>
              <Button
                onClick={onPayOnDelivery}
                variant="outline"
                className="w-full bg-transparent border-[#2A0F3D] text-[#A3C7A8] hover:bg-[#2A0F3D] hover:text-[#39FF14] font-mono uppercase tracking-wider rounded-none h-12"
                data-testid="cashapp-prepay-pay-on-delivery"
              >
                Pay on Delivery Instead
              </Button>
              <Button
                onClick={onClose}
                variant="ghost"
                className="text-[#6B8E73] hover:bg-[#2A0F3D] hover:text-[#A3C7A8] font-mono uppercase rounded-none h-9 text-xs"
                data-testid="cashapp-prepay-cancel"
              >
                <X className="w-3 h-3 mr-1" /> Cancel
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const ink = (n) => ({ color: `var(--am-ink-${n})` });

function ModernCashAppModal({ open, onClose, amount, orderShortId, render, loading, onConfirmPaid, onPayOnDelivery }) {
  const configured = render?.configured;
  const total = `$${Number(amount).toFixed(2)}`;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="am-modern-scope bg-white border-[var(--am-hair)] rounded-[20px] max-w-md max-h-[92dvh] overflow-y-auto" style={{ fontFamily: 'var(--am-font-sans)' }} data-testid="cashapp-prepay-modal">
        <DialogTitle className="text-xl font-semibold" style={ink(1)}>Complete your payment</DialogTitle>
        <DialogDescription className="text-sm" style={ink(3)}>Pay with Cash App, then confirm below.</DialogDescription>
        {loading && <div className="text-sm py-8 text-center" style={ink(3)} data-testid="cashapp-prepay-loading">Generating payment link…</div>}
        {!loading && !configured && (
          <div className="text-sm p-4 rounded-[10px] bg-red-50 border border-red-200 text-red-700" data-testid="cashapp-prepay-unconfigured">
            Cash App isn't set up right now. Please pay on delivery or contact us.
          </div>
        )}
        {!loading && configured && (
          <div className="space-y-4">
            <div className="rounded-[14px] p-4" style={{ background: 'var(--am-card-alt)', border: '1px solid var(--am-hair)' }}>
              <p className="am-label">Pay to</p>
              <p className="text-2xl font-semibold mt-1" style={ink(1)}>{render?.display_name || 'Ash & Merch'}</p>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div><p className="am-label">Total</p><p className="text-xl font-semibold am-tnum" style={ink(1)}>{total}</p></div>
                <div><p className="am-label">Order #</p><p className="text-xl font-semibold am-tnum" style={ink(1)}>{orderShortId}</p></div>
              </div>
            </div>
            <a href={render.deep_link} target="_blank" rel="noopener noreferrer" className="am-cta w-full h-14 text-base inline-flex items-center justify-center gap-2" data-testid="cashapp-prepay-deeplink">
              <Smartphone className="w-5 h-5" />Pay with Cash App
            </a>
            <div className="flex flex-col items-center gap-2 py-1">
              <p className="text-xs" style={ink(3)}>Or scan with another device</p>
              {render.qr_data_url && <img src={render.qr_data_url} alt="Cash App payment QR code" className="w-44 h-44 rounded-[10px] border p-2 bg-white" style={{ borderColor: 'var(--am-hair)' }} data-testid="cashapp-prepay-qr" />}
              <p className="text-xs" style={ink(3)}>Sending to {render.handle}</p>
            </div>
            <div className="rounded-[10px] p-3 text-sm" style={{ background: 'var(--am-emerald-50)', color: 'var(--am-emerald-800)' }}>
              Send <strong>{total}</strong> and include <strong>Order-{orderShortId}</strong> in the note so we can match it.
            </div>
            <div className="flex flex-col gap-2 pt-1">
              <button type="button" onClick={onConfirmPaid} className="am-cta w-full h-12 text-sm inline-flex items-center justify-center gap-2" data-testid="cashapp-prepay-confirm">
                <CheckCircle2 className="w-4 h-4" />I've sent the payment
              </button>
              <button type="button" onClick={onPayOnDelivery} className="am-btn-ghost w-full h-12" data-testid="cashapp-prepay-pay-on-delivery">Pay on delivery instead</button>
              <button type="button" onClick={onClose} className="text-sm h-9 inline-flex items-center justify-center gap-1 hover:underline" style={ink(3)} data-testid="cashapp-prepay-cancel">
                <X className="w-3.5 h-3.5" />Cancel
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
